import React, { useEffect, useMemo, useState } from "react";
import Input from "../../components/ui/input.tsx";
import Label from "../../components/ui/label.tsx";
import Button from "../../components/ui/button.tsx";
import { SimpleSelect } from "../../components/ui/select-shadcn.tsx";
import { useCurrencyList } from "../../hooks/useCurrencyList.tsx";

const Profile: React.FC = () => {
  const userRaw =
    typeof window !== "undefined" ? localStorage.getItem("user") : null;
  const user = useMemo(() => (userRaw ? JSON.parse(userRaw) : {}), [userRaw]);

  const [firstName, setFirstName] = useState(user?.first_name || "");
  const [lastName, setLastName] = useState(user?.last_name || "");
  const [userName, setUserName] = useState(user?.user_name || "");
  const [email] = useState(user?.email || "");
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileSaved, setProfileSaved] = useState<string | null>(null);

  const { currencies } = useCurrencyList();
  const [baseCurrency, setBaseCurrency] = useState(
    (typeof window !== "undefined" &&
      localStorage.getItem("pref:baseCurrency")) ||
      "AED"
  );
  const [timezone, setTimezone] = useState(
    (typeof window !== "undefined" && localStorage.getItem("pref:timezone")) ||
      Intl.DateTimeFormat().resolvedOptions().timeZone ||
      "UTC"
  );
  const [theme, setTheme] = useState(
    (typeof window !== "undefined" && localStorage.getItem("pref:theme")) ||
      "system"
  );
  const [prefsSaved, setPrefsSaved] = useState<string | null>(null);

  useEffect(() => {
    setFirstName(user?.first_name || "");
    setLastName(user?.last_name || "");
    setUserName(user?.user_name || "");
  }, [userRaw, user]);

  const currencyOptions = useMemo(
    () =>
      currencies.map((c) => ({
        label: `${c.code} — ${c.name}`,
        value: c.code,
      })),
    [currencies]
  );

  const timezoneOptions = useMemo(() => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return [
      { label: `System (${tz})`, value: tz || "system" },
      { label: "UTC", value: "UTC" },
    ];
  }, []);

  const themeOptions = [
    { label: "System", value: "system" },
    { label: "Dark", value: "dark" },
    { label: "Light", value: "light" },
  ];

  const saveProfile = async () => {
    setSavingProfile(true);
    setProfileSaved(null);
    try {
      const next = {
        ...user,
        first_name: firstName,
        last_name: lastName,
        user_name: userName,
      };
      localStorage.setItem("user", JSON.stringify(next));
      setProfileSaved("Saved");
    } catch (e) {
      setProfileSaved("Failed");
    } finally {
      setSavingProfile(false);
      setTimeout(() => setProfileSaved(null), 2000);
    }
  };

  const savePrefs = () => {
    try {
      localStorage.setItem("pref:baseCurrency", baseCurrency);
      localStorage.setItem("pref:timezone", timezone);
      localStorage.setItem("pref:theme", theme);
      setPrefsSaved("Saved");
      setTimeout(() => setPrefsSaved(null), 2000);
    } catch {
      setPrefsSaved("Failed");
    }
  };
  return (
    <section className="p-5 flex justify-center">
      <div className="container-site">
        <div className="flex flex-wrap gap-5 justify-between items-center mb-4">
          <div>
            <h3 className="heading-2">Profile</h3>
            <p className="sub-heading-2">View and edit your profile details.</p>
          </div>
        </div>

        {/* Profile Summary */}
        <div className="border border-white/10 bg-white/5 backdrop-blur p-4 mb-4">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-full bg-white/10 grid place-items-center text-white text-xl font-semibold">
              {(firstName || email || "U").charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="text-white font-semibold">
                {firstName || "User"}
              </div>
              <div className="text-white/70 text-sm">{email || ""}</div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Editable Profile */}
          <div className="card-glass p-4">
            <div className="widget-header pb-2">
              <div className="widget-title">Profile Information</div>
            </div>
            <div className="grid grid-cols-1 gap-3">
              <div>
                <Label className="mb-1 block">First name</Label>
                <Input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>
              <div>
                <Label className="mb-1 block">Last name</Label>
                <Input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
              <div>
                <Label className="mb-1 block">Username</Label>
                <Input
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                />
              </div>
              <div>
                <Label className="mb-1 block">Email</Label>
                <Input value={email} disabled />
              </div>
            </div>
            <div className="mt-4 flex items-center gap-3">
              <Button onClick={saveProfile} disabled={savingProfile}>
                {savingProfile ? "Saving..." : "Save Changes"}
              </Button>
              {profileSaved && (
                <span className="text-sm text-white/70">{profileSaved}</span>
              )}
            </div>
          </div>

          {/* Preferences */}
          <div className="card-glass p-4">
            <div className="widget-header pb-2">
              <div className="widget-title">Preferences</div>
            </div>
            <div className="grid grid-cols-1 gap-3">
              <div>
                <Label className="mb-1 block">Base currency</Label>
                <SimpleSelect
                  value={baseCurrency}
                  onValueChange={setBaseCurrency}
                  options={currencyOptions}
                />
              </div>
              <div>
                <Label className="mb-1 block">Timezone</Label>
                <SimpleSelect
                  value={timezone}
                  onValueChange={setTimezone}
                  options={timezoneOptions}
                />
              </div>
              <div>
                <Label className="mb-1 block">Theme</Label>
                <SimpleSelect
                  value={theme}
                  onValueChange={setTheme}
                  options={themeOptions}
                />
              </div>
            </div>
            <div className="mt-4 flex items-center gap-3">
              <Button onClick={savePrefs}>Save Preferences</Button>
              {prefsSaved && (
                <span className="text-sm text-white/70">{prefsSaved}</span>
              )}
            </div>
          </div>
        </div>

        {/* Fixed Details */}
        <div className="card-glass p-4 mt-4">
          <div className="widget-header pb-2">
            <div className="widget-title">Account Details</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-white/90">
            <div>
              <div className="text-white/60 text-xs">User ID</div>
              <div className="text-sm break-all">{user?.id || "—"}</div>
            </div>
            <div>
              <div className="text-white/60 text-xs">Email</div>
              <div className="text-sm">{email || "—"}</div>
            </div>
            <div>
              <div className="text-white/60 text-xs">Email Verified</div>
              <div className="text-sm">
                {user?.is_email_verified === false ? "No" : "Yes"}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Profile;
